import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, finalize, of, switchMap, tap } from 'rxjs';

import {
  AuthService,
  LocationModel,
  RoleType,
  RoomFlatModel,
  RoomHistoryEntryModel,
  RoomLayoutUploadApiRequestModel,
  RoomLayoutsModel,
  RoomQueryParams,
  RoomServicesModel,
  RoomSettingModel,
  RoomSettingsApiRequestModel,
  RoomsApiService,
  RoomsModel,
} from '@bookly/shared';

export interface RoomFilters {
  roomName?: string;
  capacity?: number;
  siteName?: string[];
  buildingName?: string[];
  floorName?: string[];
  roomType?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class RoomsService {
  readonly #authService = inject(AuthService);
  readonly #roomsApiService = inject(RoomsApiService);
  readonly #roomsSignal = signal<RoomsModel[] | null>(null);
  readonly #roomLayoutsSignal = signal<RoomLayoutsModel[]>([]);
  readonly #roomServicesSignal = signal<RoomServicesModel[]>([]);
  readonly #isRoomLayoutsLoadingSignal = signal<boolean>(false);
  readonly #isRoomServicesLoadingSignal = signal<boolean>(false);
  readonly #isRoomListLoadingSignal = signal<boolean>(false);
  readonly #isRoomDetailsLoadingSignal = signal<boolean>(false);
  readonly #locationsSignal = signal<LocationModel[]>([]);
  readonly #isLocationsLoadingSignal = signal<boolean>(false);
  readonly #flatRoomsSignal = signal<RoomFlatModel[]>([]);
  readonly #isFlatRoomsLoadingSignal = signal<boolean>(false);
  readonly #roomHistorySignal = signal<RoomHistoryEntryModel[]>([]);
  readonly #isRoomHistoryLoadingSignal = signal<boolean>(false);
  readonly #roomFiltersSignal = signal<RoomFilters>({});

  readonly #filteredRoomsSignal = computed(() => {
    const rooms = this.#roomsSignal();
    const filters = this.#roomFiltersSignal();

    if (!rooms) {
      return null;
    }

    return rooms.filter(room => {
      if (
        filters.roomName &&
        !room.name.toLowerCase().includes(filters.roomName.toLowerCase())
      ) {
        return false;
      }
      if (filters.capacity && room.capacity < filters.capacity) {
        return false;
      }
      if (
        filters.siteName?.length &&
        !filters.siteName.includes(room.siteName)
      ) {
        return false;
      }
      if (
        filters.buildingName?.length &&
        !filters.buildingName.includes(room.buildingName)
      ) {
        return false;
      }
      if (
        filters.floorName?.length &&
        !filters.floorName.includes(room.floorName)
      ) {
        return false;
      }
      return !(
        filters.roomType?.length && !filters.roomType.includes(room.type)
      );
    });
  });

  readonly #siteNamesSignal = computed(() => {
    const rooms = this.#roomsSignal();
    if (!rooms) {
      return [];
    }
    return Array.from(
      new Set(rooms.map(room => room.siteName).filter(Boolean))
    ).sort();
  });

  readonly #buildingNamesSignal = computed(() => {
    const rooms = this.#roomsSignal();
    if (!rooms) {
      return [];
    }
    const filters = this.#roomFiltersSignal();
    const filtered = filters.siteName?.length
      ? rooms.filter(room => filters.siteName!.includes(room.siteName))
      : rooms;
    return Array.from(
      new Set(filtered.map(room => room.buildingName).filter(Boolean))
    ).sort();
  });

  readonly #floorNamesSignal = computed(() => {
    const rooms = this.#roomsSignal();
    if (!rooms) {
      return [];
    }
    const filters = this.#roomFiltersSignal();
    let filtered = rooms;
    if (filters.siteName?.length) {
      filtered = filtered.filter(room =>
        filters.siteName!.includes(room.siteName)
      );
    }
    if (filters.buildingName?.length) {
      filtered = filtered.filter(room =>
        filters.buildingName!.includes(room.buildingName)
      );
    }
    return Array.from(
      new Set(filtered.map(room => room.floorName).filter(Boolean))
    ).sort();
  });

  readonly #roomTypesSignal = computed(() => {
    const rooms = this.#roomsSignal();
    if (!rooms) {
      return [];
    }
    return Array.from(
      new Set(rooms.map(room => room.type).filter(Boolean))
    ).sort();
  });

  get rooms() {
    return this.#roomsSignal.asReadonly();
  }

  public getRoomByDexId(roomDexId: string): RoomsModel | null {
    const rooms = this.#roomsSignal();
    return rooms?.find(room => room.roomDexId === roomDexId) ?? null;
  }

  get filteredRooms() {
    return this.#filteredRoomsSignal;
  }

  get siteNames() {
    return this.#siteNamesSignal;
  }

  get buildingNames() {
    return this.#buildingNamesSignal;
  }

  get floorNames() {
    return this.#floorNamesSignal;
  }

  get roomTypes() {
    return this.#roomTypesSignal;
  }

  get roomLayouts() {
    return this.#roomLayoutsSignal.asReadonly();
  }

  get roomServices() {
    return this.#roomServicesSignal.asReadonly();
  }

  get isRoomListLoading() {
    return this.#isRoomListLoadingSignal.asReadonly();
  }

  get isRoomDetailsLoading() {
    return this.#isRoomDetailsLoadingSignal.asReadonly();
  }

  get isRoomLayoutsLoading() {
    return this.#isRoomLayoutsLoadingSignal.asReadonly();
  }

  get isRoomServicesLoading() {
    return this.#isRoomServicesLoadingSignal.asReadonly();
  }

  get locations() {
    return this.#locationsSignal.asReadonly();
  }

  get isLocationsLoading() {
    return this.#isLocationsLoadingSignal.asReadonly();
  }

  get flatRooms() {
    return this.#flatRoomsSignal.asReadonly();
  }

  get isFlatRoomsLoading() {
    return this.#isFlatRoomsLoadingSignal.asReadonly();
  }

  get roomHistory() {
    return this.#roomHistorySignal.asReadonly();
  }

  get isRoomHistoryLoading() {
    return this.#isRoomHistoryLoadingSignal.asReadonly();
  }

  public getRooms(roleType: RoleType | null) {
    if (roleType !== null) {
      this.#isRoomListLoadingSignal.set(true);
      return this.#roomsApiService.getRoomsByEmail(roleType).pipe(
        switchMap(roomIds =>
          this.#roomsApiService.getRoomsByRoomIds(roomIds.roomDexIds ?? [])
        ),
        tap(rooms => this.#roomsSignal.set(rooms)),
        finalize(() => this.#isRoomListLoadingSignal.set(false))
      );
    }
    return of([]);
  }

  public getRoomSettings(roomDexId: string): Observable<RoomSettingModel> {
    this.#isRoomDetailsLoadingSignal.set(true);
    this.#roomLayoutsSignal.set([]);
    return this.#roomsApiService.getRoomSettings(roomDexId).pipe(
      tap(roomSettings =>
        this.#roomLayoutsSignal.set(
          roomSettings.manageSpaceConfiguration?.layouts ?? []
        )
      ),
      finalize(() => this.#isRoomDetailsLoadingSignal.set(false))
    );
  }

  public loadRoomHistory(
    roomDexId: string
  ): Observable<RoomHistoryEntryModel[]> {
    this.#roomHistorySignal.set([]);
    this.#isRoomHistoryLoadingSignal.set(true);
    return this.#roomsApiService.getRoomHistory(roomDexId).pipe(
      tap(history => this.#roomHistorySignal.set(history)),
      finalize(() => this.#isRoomHistoryLoadingSignal.set(false))
    );
  }

  public getRoomServices(): Observable<RoomServicesModel[]> {
    if (this.#roomServicesSignal().length > 0) {
      return of(this.#roomServicesSignal());
    }
    this.#isRoomServicesLoadingSignal.set(true);
    return this.#roomsApiService.getRoomServices().pipe(
      tap(services => this.#roomServicesSignal.set(services)),
      finalize(() => this.#isRoomServicesLoadingSignal.set(false))
    );
  }

  public updateRoomSettings(
    roomDexId: string,
    roomSettings: RoomSettingsApiRequestModel
  ): Observable<RoomSettingModel> {
    this.#isRoomDetailsLoadingSignal.set(true);
    return this.#roomsApiService
      .updateRoomSettings(roomDexId, roomSettings)
      .pipe(
        finalize(() => {
          this.#isRoomDetailsLoadingSignal.set(false);
        })
      );
  }

  public uploadLayout(
    layout: Omit<RoomLayoutUploadApiRequestModel, 'adminEmail'>
  ) {
    const layoutRequest: RoomLayoutUploadApiRequestModel = {
      ...layout,
      adminEmail: this.#authService.user?.email || '',
    };

    return this.#roomsApiService.uploadLayout(layoutRequest);
  }

  public uploadLayoutFile(fileUrl: string, file: File) {
    return this.#roomsApiService.uploadLayoutFile(fileUrl, file).pipe(
      catchError(err => {
        throw err;
      })
    );
  }

  public updateLayout(
    layoutId: string,
    layout: Omit<RoomLayoutUploadApiRequestModel, 'adminEmail'>
  ) {
    const layoutRequest: RoomLayoutUploadApiRequestModel = {
      ...layout,
      adminEmail: this.#authService.user?.email || '',
    };

    return this.#roomsApiService.updateLayout(layoutId, layoutRequest);
  }

  public deleteLayout(layoutId: string) {
    return this.#roomsApiService.deleteLayout(layoutId);
  }

  public getLocations() {
    this.#isLocationsLoadingSignal.set(true);
    return this.#roomsApiService.getLocations().pipe(
      tap(locations => this.#locationsSignal.set(locations)),
      finalize(() => this.#isLocationsLoadingSignal.set(false))
    );
  }

  public getFlatRooms(queryParams?: RoomQueryParams) {
    this.#isFlatRoomsLoadingSignal.set(true);
    return this.#roomsApiService.getFlatRooms(queryParams).pipe(
      tap(rooms => this.#flatRoomsSignal.set(rooms.content)),
      finalize(() => this.#isFlatRoomsLoadingSignal.set(false))
    );
  }

  public setRoomFilters(filters: RoomFilters) {
    this.#roomFiltersSignal.set(filters);
  }

  public clearRoomFilters() {
    this.#roomFiltersSignal.set({});
  }
}
